import React, { useState, useEffect } from 'react';
import { apiService, PickupPoint } from '../services/api';

const PickupPoints: React.FC = () => {
  const [pickupPoints, setPickupPoints] = useState<PickupPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPickupPoint, setEditingPickupPoint] = useState<PickupPoint | null>(null);
  const [duplicates, setDuplicates] = useState<PickupPoint[][]>([]);
  const [showDuplicates, setShowDuplicates] = useState(false);

  // Form state
  const [formData, setFormData] = useState<Omit<PickupPoint, 'id' | 'isActive' | 'createdAt' | 'updatedAt'>>({
    name: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    phone: '',
    email: '',
    contactPerson: '',
    businessHours: '',
    notes: '',
  });

  useEffect(() => {
    fetchPickupPoints();
  }, []);

  const fetchPickupPoints = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAllPickupPoints(100, 0, false);
      setPickupPoints(response);
    } catch (err) {
      console.error('Failed to fetch pickup points:', err);
      setError('Failed to load pickup points');
    } finally {
      setLoading(false);
    }
  };

  const searchPickupPoints = async () => {
    if (!searchQuery.trim()) {
      fetchPickupPoints();
      return;
    }

    try {
      setLoading(true);
      const results = await apiService.searchPickupPoints(searchQuery, 50);
      setPickupPoints(results);
    } catch (err) {
      console.error('Failed to search pickup points:', err);
      setError('Failed to search pickup points');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    searchPickupPoints();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      postalCode: '',
      country: '',
      phone: '',
      email: '',
      contactPerson: '',
      businessHours: '',
      notes: '',
    });
    setEditingPickupPoint(null);
    setShowCreateForm(false);
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiService.createPickupPoint(formData);
      resetForm();
      fetchPickupPoints();
    } catch (err) {
      console.error('Failed to create pickup point:', err);
      alert('Failed to create pickup point');
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPickupPoint?.id) return;

    try {
      await apiService.updatePickupPoint(editingPickupPoint.id, formData);
      resetForm();
      fetchPickupPoints();
    } catch (err) {
      console.error('Failed to update pickup point:', err);
      alert('Failed to update pickup point');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this pickup point?')) return;

    try {
      await apiService.deletePickupPoint(id);
      fetchPickupPoints();
    } catch (err) {
      console.error('Failed to delete pickup point:', err);
      alert('Failed to delete pickup point');
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await apiService.deactivatePickupPoint(id);
      fetchPickupPoints();
    } catch (err) {
      console.error('Failed to deactivate pickup point:', err);
      alert('Failed to deactivate pickup point');
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await apiService.activatePickupPoint(id);
      fetchPickupPoints();
    } catch (err) {
      console.error('Failed to activate pickup point:', err);
      alert('Failed to activate pickup point');
    }
  };

  const startEdit = (pickupPoint: PickupPoint) => {
    setEditingPickupPoint(pickupPoint);
    setFormData({
      name: pickupPoint.name,
      address: pickupPoint.address,
      city: pickupPoint.city,
      postalCode: pickupPoint.postalCode,
      country: pickupPoint.country,
      phone: pickupPoint.phone,
      email: pickupPoint.email,
      contactPerson: pickupPoint.contactPerson,
      businessHours: pickupPoint.businessHours,
      notes: pickupPoint.notes,
    });
    setShowCreateForm(true);
  };

  const findDuplicates = async () => {
    try {
      const response = await apiService.findDuplicates();
      setDuplicates(response);
      setShowDuplicates(true);
    } catch (err) {
      console.error('Failed to find duplicates:', err);
      alert('Failed to find duplicates');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading pickup points...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Pickup Points</h1>
        <p className="text-gray-600">Manage pickup points for wine collection</p>
      </div>

      {/* Search and Actions */}
      <div className="mb-6 flex flex-wrap gap-4 items-center justify-between">
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search pickup points..."
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <button
            type="submit"
            className="px-4 py-2 text-white rounded-md transition-colors"
            style={{ backgroundColor: '#2563eb', border: 'none' }}
          >
            Search
          </button>
        </form>

        <div className="flex gap-4">
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 text-white rounded-md transition-colors"
            style={{ backgroundColor: '#059669', border: 'none' }}
          >
            Add New
          </button>
          <button
            onClick={findDuplicates}
            className="px-4 py-2 text-white rounded-md transition-colors"
            style={{ backgroundColor: '#ea580c', border: 'none' }}
          >
            Find Duplicates
          </button>
          <button
            onClick={() => {
              setSearchQuery('');
              fetchPickupPoints();
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-4">
            {editingPickupPoint ? 'Edit Pickup Point' : 'Create New Pickup Point'}
          </h2>
          <form onSubmit={editingPickupPoint ? handleEdit : handleCreate} className="space-y-4">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person *</label>
                <input
                  type="text"
                  value={formData.contactPerson}
                  onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code *</label>
                <input
                  type="text"
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Hours</label>
              <input
                type="text"
                value={formData.businessHours}
                onChange={(e) => handleInputChange('businessHours', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
                placeholder="e.g., Mon-Fri 9AM-6PM, Sat 10AM-4PM"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
              />
            </div>
            <div className="flex gap-4">
              <button
                type="submit"
                className="px-4 py-2 text-white rounded-md transition-colors"
                style={{ backgroundColor: '#059669', border: 'none' }}
              >
                {editingPickupPoint ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Duplicates Modal */}
      {showDuplicates && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Duplicate Pickup Points</h2>
              <button
                onClick={() => setShowDuplicates(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            {duplicates.length === 0 ? (
              <p className="text-gray-600">No duplicates found!</p>
            ) : (
              <div className="space-y-4">
                {duplicates.map((group, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold mb-2">Duplicate Group {index + 1}</h3>
                    <div className="space-y-2">
                      {group.map((pickupPoint) => (
                        <div key={pickupPoint.id} className="bg-gray-50 p-3 rounded">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{pickupPoint.name}</p>
                              <p className="text-sm text-gray-600">
                                {pickupPoint.address}, {pickupPoint.city}, {pickupPoint.postalCode}
                              </p>
                              <p className="text-sm text-gray-500">
                                Contact: {pickupPoint.contactPerson} • {pickupPoint.phone}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => startEdit(pickupPoint)}
                                className="text-blue-600 hover:text-blue-800 text-sm"
                              >
                                Edit
                              </button>
                              {pickupPoint.isActive ? (
                                <button
                                  onClick={() => pickupPoint.id && handleDeactivate(pickupPoint.id)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  Deactivate
                                </button>
                              ) : (
                                <button
                                  onClick={() => pickupPoint.id && handleActivate(pickupPoint.id)}
                                  className="text-green-600 hover:text-green-900 text-sm"
                                >
                                  Activate
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pickup Points Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pickupPoints.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No pickup points found
                  </td>
                </tr>
              ) : (
                pickupPoints.map((pickupPoint) => (
                  <tr key={pickupPoint.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {pickupPoint.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div>{pickupPoint.address}</div>
                        <div className="text-gray-500">
                          {pickupPoint.city}, {pickupPoint.postalCode}, {pickupPoint.country}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div>{pickupPoint.contactPerson}</div>
                        <div className="text-gray-500">{pickupPoint.phone}</div>
                        <div className="text-gray-500">{pickupPoint.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        pickupPoint.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {pickupPoint.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {pickupPoint.createdAt ? formatDate(pickupPoint.createdAt) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => startEdit(pickupPoint)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Edit
                        </button>
                        {pickupPoint.isActive ? (
                          <button
                            onClick={() => pickupPoint.id && handleDeactivate(pickupPoint.id)}
                            className="text-yellow-600 hover:text-yellow-900"
                          >
                            Deactivate
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => pickupPoint.id && handleActivate(pickupPoint.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Activate
                            </button>
                            <button
                              onClick={() => pickupPoint.id && handleDelete(pickupPoint.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PickupPoints;
